import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { CalendarIcon, CheckIcon } from 'lucide-react';
export function PreEventSeries() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      // Convert form data to URL parameters for Google Apps Script
      const params = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formData.date
      });
      const response = await fetch(`https://script.google.com/macros/s/AKfycbyQS1cbp5LLww4nxUqzw8orRVVHYo5nNGaJ3zA-Gb4bpx_pu4dPV3hxajrJKRsjnhR0/exec?${params.toString()}`, {
        method: 'GET',
        redirect: 'follow'
      });
      const result = await response.json();
      if (result.success || result.result === 'success') {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: ''
        });
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError(result.error || result.message || 'There was an error submitting your registration. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Unable to connect to the registration system. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  // Generate all Friday and Saturday dates from Dec 5, 2025 to Jan 3, 2026
  const generateDates = () => {
    const dates = [];
    const startDate = new Date('2025-12-05');
    const endDate = new Date('2026-01-03');
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      // 5 = Friday, 6 = Saturday
      if (dayOfWeek === 5 || dayOfWeek === 6) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };
  const availableDates = generateDates();
  return <Layout data-id="element-366">
      <div className="max-w-7xl mx-auto px-6 py-16" data-id="element-367">
        {/* Hero Section */}
        <div className="text-center mb-16" data-id="element-368">
          <div className="inline-block bg-voices-red text-white px-6 py-2 rounded-full font-bold mb-4" data-id="element-369">
            PRE-EVENT SERIES
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-black mb-6 leading-tight" data-id="element-370">
            Be Part of the <span className="text-voices-red" data-id="element-371">Story</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed" data-id="element-372">
            Join exclusive Instagram Live sessions leading up to Voices with
            Victoria Live 2026
          </p>
        </div>
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-373">
          {/* Left Column - Information */}
          <div className="space-y-8" data-id="element-374">
            {/* About the Series */}
            <div className="bg-gradient-to-br from-voices-cream to-white rounded-2xl p-8 border-l-8 border-voices-gold" data-id="element-375">
              <h2 className="text-3xl font-bold text-black mb-4" data-id="element-376">
                What Is This?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4" data-id="element-377">
                The Pre-Event Series is your opportunity to contribute to
                something bigger than yourself. Through intimate Instagram Live
                sessions, you'll share your story, inspire our growing
                community, and help drive the impact of Voices with Victoria
                Live 2026.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-378">
                This isn't just about attending — it's about{' '}
                <strong data-id="element-379">contributing your voice</strong> to a movement that's
                shaping the future of young professionals and creatives across
                Africa.
              </p>
            </div>
            {/* Why Join */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-voices-red" data-id="element-380">
              <h2 className="text-2xl font-bold text-black mb-6" data-id="element-381">
                Why Join Our Sessions?
              </h2>
              <div className="space-y-4" data-id="element-382">
                <div className="flex items-start gap-3" data-id="element-383">
                  <CheckIcon className="text-voices-gold mt-1 flex-shrink-0" size={24} data-id="element-384" />
                  <div data-id="element-385">
                    <h3 className="font-bold text-black mb-1" data-id="element-386">
                      Share Your Story
                    </h3>
                    <p className="text-gray-700" data-id="element-387">
                      Your journey matters. Share how you're navigating life's
                      transitions and inspire others who are on similar paths.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-388">
                  <CheckIcon className="text-voices-gold mt-1 flex-shrink-0" size={24} data-id="element-389" />
                  <div data-id="element-390">
                    <h3 className="font-bold text-black mb-1" data-id="element-391">Drive Impact</h3>
                    <p className="text-gray-700" data-id="element-392">
                      Help build momentum for the main event. Your contribution
                      creates ripples that reach thousands.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-393">
                  <CheckIcon className="text-voices-gold mt-1 flex-shrink-0" size={24} data-id="element-394" />
                  <div data-id="element-395">
                    <h3 className="font-bold text-black mb-1" data-id="element-396">
                      Intimate Conversations
                    </h3>
                    <p className="text-gray-700" data-id="element-397">
                      Only 3 participants per session means authentic,
                      meaningful dialogue with Victoria and fellow changemakers.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-398">
                  <CheckIcon className="text-voices-gold mt-1 flex-shrink-0" size={24} data-id="element-399" />
                  <div data-id="element-400">
                    <h3 className="font-bold text-black mb-1" data-id="element-401">
                      Be Part of the Movement
                    </h3>
                    <p className="text-gray-700" data-id="element-402">
                      Connect with the Voices with Victoria community before the
                      big event on January 10th, 2026.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Event Info */}
            <div className="bg-voices-navy text-white rounded-2xl p-8" data-id="element-403">
              <h3 className="text-2xl font-bold mb-4" data-id="element-404">About the Main Event</h3>
              <p className="text-lg mb-4" data-id="element-405">
                <strong data-id="element-406">Voices with Victoria Live 2026</strong>
              </p>
              <p className="mb-4" data-id="element-407">
                Saturday, January 10th, 2026 at The Somi Place, Lekki
              </p>
              <p className="text-voices-cream leading-relaxed" data-id="element-408">
                A faith-forward gathering for the searching, the starting, the
                scaling, the surrendered, and the stewarding. This is where it
                shifts.
              </p>
            </div>
          </div>
          {/* Right Column - Registration Form */}
          <div data-id="element-409">
            <div className="bg-white rounded-2xl p-8 shadow-2xl border-l-8 border-voices-red sticky top-24" data-id="element-410">
              <h2 className="text-3xl font-bold text-black mb-2" data-id="element-411">
                Register for a Session
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-412">
                Choose your preferred date and join us live on Instagram
              </p>
              {submitted && <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded" data-id="element-413">
                  <p className="text-green-800 font-semibold" data-id="element-414">
                    ✓ Registration successful! We'll send you the Instagram Live
                    link closer to your session date.
                  </p>
                </div>}
              {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded" data-id="element-415">
                  <p className="text-red-800" data-id="element-416">{error}</p>
                </div>}
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-417">
                {/* Name Field */}
                <div data-id="element-418">
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2" data-id="element-419">
                    Your Name
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-voices-red focus:border-voices-red" placeholder="Enter your full name" required disabled={isSubmitting} data-id="element-420" />
                </div>
                {/* Email Field */}
                <div data-id="element-421">
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2" data-id="element-422">
                    Email Address
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-voices-red focus:border-voices-red" placeholder="your.email@example.com" required disabled={isSubmitting} data-id="element-423" />
                </div>
                {/* Phone Number Field */}
                <div data-id="element-424">
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2" data-id="element-425">
                    Phone Number
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-voices-red focus:border-voices-red" placeholder="+234 800 000 0000" required disabled={isSubmitting} data-id="element-426" />
                </div>
                {/* Date Selection */}
                <div data-id="element-427">
                  <label htmlFor="date" className="block text-gray-700 font-semibold mb-2" data-id="element-428">
                    Choose Your Session Date
                  </label>
                  <select id="date" name="date" value={formData.date} onChange={handleChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-voices-red focus:border-voices-red bg-white" required disabled={isSubmitting} data-id="element-429">
                    <option value="" data-id="element-430">Select a date...</option>
                    {availableDates.map(date => {
                    const dateString = date.toISOString().split('T')[0];
                    const dayName = date.toLocaleDateString('en-US', {
                      weekday: 'long'
                    });
                    const formattedDate = date.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    return <option key={dateString} value={dateString} data-id="element-431">
                          {dayName}, {formattedDate}
                        </option>;
                  })}
                  </select>
                  <p className="text-sm text-gray-500 mt-2" data-id="element-432">
                    <CalendarIcon size={16} className="inline mr-1" data-id="element-433" />
                    Fridays and Saturdays from Dec 5, 2025 to Jan 3, 2026
                  </p>
                </div>
                {/* Submit Button */}
                <button type="submit" disabled={isSubmitting} className="w-full bg-voices-red text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed" data-id="element-434">
                  {isSubmitting ? 'Submitting...' : 'Reserve My Spot'}
                </button>
              </form>
              {/* Available Dates Info */}
              <div className="mt-6 p-4 bg-voices-cream rounded-lg" data-id="element-435">
                <p className="text-sm text-gray-700" data-id="element-436">
                  <CalendarIcon size={16} className="inline mr-1" data-id="element-437" />
                  <strong data-id="element-438">{availableDates.length}</strong> session dates
                  available
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-br from-voices-red to-red-700 text-white rounded-2xl p-12 text-center max-w-4xl mx-auto" data-id="element-439">
          <h2 className="text-3xl font-bold mb-4" data-id="element-440">This Is Where It Shifts</h2>
          <p className="text-xl mb-6" data-id="element-441">
            Your story matters. Your voice matters. Join us and be part of the
            movement.
          </p>
          <p className="text-lg text-voices-cream" data-id="element-442">
            #VoicesWithVictoria | #ThisIsWhereItShifts
          </p>
        </div>
      </div>
    </Layout>;
}